import React, { useState, useMemo } from 'react';
import type { TeamMemberApplications } from '../types';
import { formatDecimalHours } from '../services/personalizedDataService';

interface NonAIApplicationsTableProps {
  teamMembers: TeamMemberApplications[];
}

type SortableColumn = 'name' | 'totalHours' | 'emailTime' | 'topCategory' | 'appCount';

const NonAIApplicationsTable: React.FC<NonAIApplicationsTableProps> = ({ teamMembers }) => {
  console.log('📊 [NON-AI-TABLE] Rendering with team members:', {
    count: teamMembers.length,
    members: teamMembers.map(m => {
      const emailTime = Object.entries(m.applications)
        .filter(([, app]) => app.category.toLowerCase().includes('email'))
        .reduce((sum, [, app]) => sum + app.hours, 0);
      const categoryTotals = m.applicationsByCategory;
      const topCategory = Object.keys(categoryTotals).length > 0 
        ? Object.keys(categoryTotals).reduce((a, b) => 
            categoryTotals[a].reduce((sum, app) => sum + app.hours, 0) > 
            categoryTotals[b].reduce((sum, app) => sum + app.hours, 0) ? a : b
          ) 
        : 'None';
      
      return {
        name: m.name,
        email: m.email,
        totalHours: m.totalNonAIHours,
        emailTime: emailTime,
        topCategory: topCategory,
        appsCount: Object.keys(m.applications).length
      };
    })
  });

  const [sortBy, setSortBy] = useState<SortableColumn>('totalHours');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());

  const sortedTeamMembers = useMemo(() => {
    if (teamMembers.length === 0) return [];

    return [...teamMembers].sort((a, b) => {
      let aValue: any;
      let bValue: any;

      switch (sortBy) {
        case 'name':
          aValue = a.name || a.email;
          bValue = b.name || b.email;
          break;
        case 'totalHours':
          aValue = a.totalNonAIHours;
          bValue = b.totalNonAIHours;
          break;
        case 'emailTime':
          // Calculate email time from applications
          aValue = Object.entries(a.applications)
            .filter(([, app]) => app.category.toLowerCase().includes('email'))
            .reduce((sum, [, app]) => sum + app.hours, 0);
          bValue = Object.entries(b.applications)
            .filter(([, app]) => app.category.toLowerCase().includes('email'))
            .reduce((sum, [, app]) => sum + app.hours, 0);
          break;
        case 'topCategory':
          // Get the category with most time
          const aCategoryTotals = a.applicationsByCategory;
          const bCategoryTotals = b.applicationsByCategory;
          aValue = Object.keys(aCategoryTotals).length > 0 
            ? Object.keys(aCategoryTotals).reduce((a, b) => 
                aCategoryTotals[a].reduce((sum, app) => sum + app.hours, 0) > 
                aCategoryTotals[b].reduce((sum, app) => sum + app.hours, 0) ? a : b
              ) 
            : '';
          bValue = Object.keys(bCategoryTotals).length > 0 
            ? Object.keys(bCategoryTotals).reduce((a, b) => 
                bCategoryTotals[a].reduce((sum, app) => sum + app.hours, 0) > 
                bCategoryTotals[b].reduce((sum, app) => sum + app.hours, 0) ? a : b
              )
            : '';
          break;
        case 'appCount':
          aValue = Object.keys(a.applications).length;
          bValue = Object.keys(b.applications).length;
          break;
        default:
          return 0;
      }

      if (typeof aValue === 'string' && typeof bValue === 'string') {
        const comparison = aValue.localeCompare(bValue);
        return sortOrder === 'asc' ? comparison : -comparison;
      }

      const comparison = aValue - bValue;
      return sortOrder === 'asc' ? comparison : -comparison;
    });
  }, [teamMembers, sortBy, sortOrder]);

  const handleSort = (column: SortableColumn) => {
    if (sortBy === column) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(column);
      setSortOrder('desc');
    }
  };

  const getSortIcon = (column: SortableColumn) => {
    if (sortBy !== column) return '↕️';
    return sortOrder === 'asc' ? '⬆️' : '⬇️';
  };

  const toggleRowExpansion = (userId: string) => {
    const newExpanded = new Set(expandedRows);
    if (newExpanded.has(userId)) {
      newExpanded.delete(userId);
    } else {
      newExpanded.add(userId);
    }
    setExpandedRows(newExpanded);
  };

  const renderApplicationList = (applications: {[appName: string]: {hours: number; category: string; frequency: number}}, category: string): React.ReactNode => {
    const categoryApps = Object.entries(applications)
      .filter(([, appData]) => appData.category === category)
      .sort(([,a], [,b]) => b.hours - a.hours);
    
    if (categoryApps.length === 0) {
      return <span className="no-apps">No {category} applications</span>;
    }

    return (
      <div className="app-list">
        {categoryApps.map(([appName, appData]) => (
          <div key={appName} className="app-item">
            <span className="app-name">{appName}</span>
            <span className="app-time">{formatDecimalHours(appData.hours)}</span>
          </div>
        ))}
      </div>
    );
  };

  const renderExpandedRow = (member: TeamMemberApplications): React.ReactNode => {
    const categories = [...new Set(Object.values(member.applications).map(app => app.category))];
    
    return (
      <tr key={`${member.userId || member.email}-expanded`} className="expanded-row">
        <td colSpan={7}>
          <div className="expanded-content">
            <div className="app-sections">
              {categories.map(category => (
                <div key={category} className="app-section">
                  <h4>📁 {category}</h4>
                  {renderApplicationList(member.applications, category)}
                </div>
              ))}
            </div>
          </div>
        </td>
      </tr>
    );
  };

  if (teamMembers.length === 0) {
    return (
      <div className="no-data-message">
        No non-AI application data available for this period.
      </div>
    );
  }

  return (
    <table className="ai-overview-table">
      <thead>
        <tr>
          <th>Rank</th>
          <th onClick={() => handleSort('name')} className="sortable">
            Team Member {getSortIcon('name')}
          </th>
          <th onClick={() => handleSort('totalHours')} className="sortable">
            Total Non-AI Time {getSortIcon('totalHours')}
          </th>
          <th onClick={() => handleSort('emailTime')} className="sortable">
            Email Time {getSortIcon('emailTime')}
          </th>
          <th onClick={() => handleSort('topCategory')} className="sortable">
            Top Category {getSortIcon('topCategory')}
          </th>
          <th onClick={() => handleSort('appCount')} className="sortable">
            Applications {getSortIcon('appCount')}
          </th>
          <th>Actions</th>
        </tr>
      </thead>
      <tbody>
        {sortedTeamMembers.map((member, index) => (
          <React.Fragment key={member.userId || member.email}>
            <tr className={`user-row ${index < 3 ? `rank-${index + 1}` : ''}`}>
              <td className="rank-cell">
                <span className="rank-number">{index + 1}</span>
                {index === 0 && <span className="rank-badge gold">🥇</span>}
                {index === 1 && <span className="rank-badge silver">🥈</span>}
                {index === 2 && <span className="rank-badge bronze">🥉</span>}
              </td>
              <td className="team-member-cell">
                <strong>{member.name || member.email}</strong>
              </td>
              <td className="time-cell">
                <strong>{formatDecimalHours(member.totalNonAIHours)}</strong>
              </td>
              <td className="time-cell">
                {(() => {
                  const emailTime = Object.entries(member.applications)
                    .filter(([, app]) => app.category.toLowerCase().includes('email'))
                    .reduce((sum, [, app]) => sum + app.hours, 0);
                  return emailTime > 0 ? formatDecimalHours(emailTime) : '-';
                })()}
              </td>
              <td>
                {(() => {
                  const categoryTotals = member.applicationsByCategory;
                  if (Object.keys(categoryTotals).length === 0) return 'No data';
                  
                  const topCategory = Object.keys(categoryTotals).reduce((a, b) => 
                    categoryTotals[a].reduce((sum, app) => sum + app.hours, 0) > 
                    categoryTotals[b].reduce((sum, app) => sum + app.hours, 0) ? a : b
                  );
                  const topCategoryTime = categoryTotals[topCategory].reduce((sum, app) => sum + app.hours, 0);
                  return `${topCategory} (${formatDecimalHours(topCategoryTime)})`;
                })()}
              </td>
              <td className="app-count-cell">
                {Object.keys(member.applications).length} apps
              </td>
              <td className="actions-cell">
                <button
                  onClick={() => toggleRowExpansion(member.userId || member.email)}
                  className="expand-button"
                  title={expandedRows.has(member.userId || member.email) ? 'Hide details' : 'Show details'}
                >
                  {expandedRows.has(member.userId || member.email) ? '📤' : '📥'}
                </button>
              </td>
            </tr>
            {expandedRows.has(member.userId || member.email) && renderExpandedRow(member)}
          </React.Fragment>
        ))}
      </tbody>
    </table>
  );
};

export default NonAIApplicationsTable;
